import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Loader2 } from 'lucide-react';
import { employeeApi, locationApi } from '../services/api';
import type { Location, OrganizationHierarchyResponse } from '../types';
import { OrgChartBranch } from '../components/OrgChartNodes';
import { useLocationContext } from '../components/Layout';

const OrganizationChart: React.FC = () => {
  const navigate = useNavigate();
  const { selectedLocationId, setSelectedLocationId, locations } = useLocationContext();
  const [data, setData] = useState<OrganizationHierarchyResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locationDetail, setLocationDetail] = useState<Location | null>(null);

  useEffect(() => {
    if (selectedLocationId == null) {
      setData(null);
      setError(null);
      setLocationDetail(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);

    Promise.all([
      employeeApi.getOrganizationHierarchy(selectedLocationId),
      locationApi.getById(selectedLocationId).catch(() => null),
    ])
      .then(([hierarchyRes, locRes]) => {
        if (!cancelled) {
          setData(hierarchyRes);
          setLocationDetail(locRes);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const msg =
            err && typeof err === 'object' && 'response' in err
              ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
              : null;
          setError(msg || 'Could not load organization chart.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedLocationId]);

  const openProfile = (id: number) => {
    navigate(`/employees/${id}`);
  };

  const selectedLocationName =
    selectedLocationId != null ? locations.find((l) => l.id === selectedLocationId)?.name : null;

  const locationManager = locationDetail?.manager ?? locations.find((l) => l.id === selectedLocationId)?.manager;

  return (
    <div className="w-full max-w-[96%] mx-auto p-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <Link
          to="/employees"
          className="inline-flex items-center gap-2 text-neutral-600 hover:text-neutral-900 text-sm font-medium w-fit"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Employees
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div className="flex items-start gap-3 min-w-0">
          <Building2 className="h-8 w-8 text-violet-600 shrink-0 mt-1" />
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-neutral-800">Organization chart</h1>
            <p className="text-sm text-neutral-600 mt-1">
              Line manager relationships for employees at the selected location (main or sub-location).
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-1 shrink-0 w-full sm:w-auto sm:min-w-[14rem]">
          <label htmlFor="org-chart-location" className="text-xs font-medium text-neutral-500">
            Location
          </label>
          <select
            id="org-chart-location"
            value={selectedLocationId ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setSelectedLocationId(v ? Number(v) : null);
            }}
            className="w-full sm:w-56 px-3 py-2 text-sm border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
          >
            <option value="">All locations</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedLocationId != null && (
        <div className="mb-4 rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Location manager</div>
          {locationManager ? (
            <button
              type="button"
              onClick={() => openProfile(locationManager.id)}
              className="mt-1 text-left text-base font-semibold text-violet-700 hover:text-violet-900 hover:underline"
            >
              {locationManager.firstName} {locationManager.lastName}
            </button>
          ) : (
            <p className="mt-1 text-sm text-neutral-600">Not assigned</p>
          )}
          {selectedLocationName && (
            <p className="mt-1 text-xs text-neutral-500">{selectedLocationName}</p>
          )}
        </div>
      )}

      <div className="bg-white border border-neutral-200 rounded-xl p-6 shadow-sm">
        {selectedLocationId == null && (
          <div className="text-center py-16 px-4">
            <p className="text-neutral-700 font-medium">Select a location</p>
            <p className="text-sm text-neutral-500 mt-2 max-w-md mx-auto">
              Choose a location above to view the reporting hierarchy for employees at that site. &quot;All
              locations&quot; is not available here—pick one location to load the chart.
            </p>
          </div>
        )}

        {selectedLocationId != null && loading && (
          <div className="flex flex-col items-center justify-center py-24 gap-3 text-neutral-600">
            <Loader2 className="h-10 w-10 animate-spin text-violet-500" />
            <span className="text-sm">Loading organization chart…</span>
          </div>
        )}

        {selectedLocationId != null && error && !loading && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">{error}</div>
        )}

        {selectedLocationId != null && data && !loading && !error && data.roots.length === 0 && (
          <p className="text-center text-neutral-600 py-12">No employees at this location for the chart.</p>
        )}

        {selectedLocationId != null && data && !loading && !error && data.roots.length > 0 && (
          <div className="overflow-x-auto pb-2 -mx-2 px-2">
            <div
              className={
                data.roots.length === 1
                  ? 'flex justify-center min-w-min'
                  : 'flex flex-row flex-wrap justify-center gap-x-14 gap-y-12 items-start min-w-max mx-auto py-2'
              }
            >
              {data.roots.map((root) => (
                <OrgChartBranch key={root.id} node={root} onOpenProfile={openProfile} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganizationChart;
